// components/SeoPage.jsx
import React from "react";
import { Helmet } from "react-helmet-async";

export default function SeoPage() {
  const title =
    "Earn Passive Income with Turo | Become a Turo Co-Host | Diviseed";
  const desc =
    "Learn how to earn passive income with Turo or become a Turo co-host. Diviseed gives you the market data to choose the right cars, set the right prices, and grow profitably.";
  const url = "https://www.diviseed.com/turo-passive-income-cohost";
  const image = "https://www.diviseed.com/og/turo-passive-income.png";

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How do I earn passive income with Turo?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Use market data to choose high-demand cars, optimize pricing, and keep utilization high. Co-hosting lets you scale without owning all vehicles.",
        },
      },
      {
        "@type": "Question",
        name: "What is a Turo co-host?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A Turo co-host manages vehicles for other owners—handling guest communication, cleaning, delivery, and pricing—in exchange for a revenue share.",
        },
      },
      {
        "@type": "Question",
        name: "Do I need to own a car to start on Turo?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. You can begin as a Turo co-host, partnering with owners while you grow experience and capital.",
        },
      },
    ],
  };

  const breadcrumbsJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Diviseed",
        item: "https://www.diviseed.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Turo Passive Income & Co-Host",
        item: url,
      },
    ],
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 text-gray-800">
      {/* SEO head */}
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta
          name="keywords"
          content="Turo co-host, passive income, Turo passive income, Turo calculator, Turo earnings, car sharing"
        />
        <link rel="canonical" href={url} />

        {/* Open Graph */}
        <meta property="og:site_name" content="Diviseed" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={image} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content={image} />

        {/* Robots */}
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />

        {/* JSON-LD (FAQ + Breadcrumbs) */}
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbsJsonLd)}
        </script>
      </Helmet>

      {/* Page content */}
      <h1 className="text-3xl font-bold mb-6">
        Earn Passive Income with Turo — Backed by Data
      </h1>
      <p className="mb-8 text-lg">
        Want your cars to work for you? Whether you’re looking for a single side
        hustle or building a scalable fleet, <strong>Turo</strong> offers a
        unique opportunity to earn <strong>passive income</strong>. And with
        Diviseed, you don’t have to guess your way to success. We give you{" "}
        <strong>real market data</strong> to choose the right cars, set the
        right prices, and grow with confidence.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        Turn Your Car into an Income Stream
      </h2>
      <p className="mb-6">
        With Turo, you can list your car and start earning from travelers who
        need rentals in your city. But consistent profit takes more than just
        listing — it’s about knowing <strong>what works in your market</strong>.
      </p>
      <ul className="list-disc list-inside mb-8">
        <li>Which vehicles get the most bookings</li>
        <li>The average daily rates by make, model, and year</li>
        <li>Seasonal trends and demand patterns</li>
        <li>Strategies to keep your fleet booked and profitable</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">Become a Turo Co-Host</h2>
      <p className="mb-6">
        You don’t have to own all the cars in your fleet. As a{" "}
        <strong>Turo co-host</strong>, you can manage other owners’
        vehicles—handling guest communication, cleaning, delivery, and
        pricing—in exchange for a share of the earnings.
      </p>
      <ul className="list-disc list-inside mb-8">
        <li>
          Identify underperforming cars in your market and approach owners
        </li>
        <li>Track fleet-wide performance and earnings in one dashboard</li>
        <li>Optimize utilization rates for maximum ROI</li>
        <li>Build long-term relationships with vehicle owners</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">Why Data Beats Guesswork</h2>
      <p className="mb-8">
        The difference between an average host and a top-earning host?
        <strong> Information</strong>. Our analytics help you:
      </p>
      <ul className="list-disc list-inside mb-8">
        <li>Maximize utilization rates without underpricing</li>
        <li>Keep demand percentages high year-round</li>
        <li>Decide when to expand your fleet</li>
        <li>Eliminate cars that aren’t delivering returns</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        Your Path to Scalable Passive Income
      </h2>
      <p className="mb-8">
        Turo can be a <strong>true passive income</strong> stream if you treat
        it like a business. Whether you:
      </p>
      <ul className="list-disc list-inside mb-8">
        <li>Own one car</li>
        <li>Manage multiple vehicles</li>
        <li>
          Or run a <strong>Turo co-host</strong> operation
        </li>
      </ul>
      <p className="mb-8">
        Diviseed is your advantage. We give you the insights, you make the
        moves.
      </p>

      <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl">
        <p className="text-lg font-semibold">
          Ready to earn <strong>passive income</strong> with Turo or grow as a{" "}
          <strong>Turo co-host</strong>?
        </p>
        <a
          href="/signup"
          className="inline-block mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg"
        >
          Get Started with Diviseed →
        </a>
      </div>
    </div>
  );
}
